import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks';
import { useSocket } from '../hooks/useSocket';
import { StreamChannel } from '../services/channelsService';
import {
  DestinationHealthEnvelope,
  HlsSessionStatusEnvelope,
  PullOutputStatusEnvelope,
  RuntimeAlarm,
  RuntimeHealth,
  RuntimeSnapshot,
  StreamAnalytics,
  StreamStats,
  Transcoder,
  TranscoderRuntimeEnvelope,
  TranscoderStatusMap,
  getRuntimeSnapshot,
} from '../services/streamsService';
import { estimatePayloadBytes, recordRuntimeTelemetryDebug } from '../utils/runtimeTelemetryDebug';
import {
  getOutputAlarmKeys,
  reconcileAlarmsWithSnapshot,
  reconcileOutputHealthAlarm,
} from '../utils/runtimeAlarmContract';

const REFOCUS_SNAPSHOT_DELAY_MS = 750;
const REFOCUS_SOCKET_COALESCE_MS = 100;
const RUNTIME_EVENT_STALE_MS = 30000;

export type AudioAlertState = 'unarmed' | 'armed' | 'blocked';

export interface RuntimeChannelState {
  channels: StreamChannel[];
  activeChannel: StreamChannel | null;
  setActiveChannelId: (id: number) => void;
}

export interface RuntimeStatsState {
  stats: StreamStats | null;
  analytics: StreamAnalytics | null;
  isStreamLive: boolean;
}

export interface RuntimeOutputState {
  transcoders: Transcoder[];
  pullStatuses: Record<number, PullOutputStatusEnvelope>;
  hlsStatuses: Record<number, HlsSessionStatusEnvelope>;
  outputHealthStatuses: Record<number, DestinationHealthEnvelope>;
}

export interface RuntimeHealthState {
  serverHealth: RuntimeHealth | null;
  serverTime: string | null;
  loading: boolean;
  isConnected: boolean;
  isAccountDisabled: boolean;
}

export interface RuntimeAccessState {
  isAccountDisabled: boolean;
}

export interface RuntimeAlarmState {
  runtimeAlarmsByKey: Record<string, RuntimeAlarm>;
  activeAlarms: RuntimeAlarm[];
  lastAlarmAt: string | null;
  mutedAlarmOutputIds: Record<number, boolean>;
  audioAlertState: AudioAlertState;
  armAudioAlerts: () => void;
  toggleOutputAlarmMute: (outputId: number) => void;
  clearOutputAlarmMute: (outputId: number) => void;
}

export interface RuntimeActionsState {
  refreshData: () => Promise<void>;
}

export interface RuntimeStatusState extends RuntimeChannelState, RuntimeStatsState, RuntimeOutputState, RuntimeHealthState, RuntimeAlarmState, RuntimeActionsState {}

const RuntimeChannelContext = createContext<RuntimeChannelState | undefined>(undefined);
const RuntimeStatsContext = createContext<RuntimeStatsState | undefined>(undefined);
const RuntimeOutputContext = createContext<RuntimeOutputState | undefined>(undefined);
const RuntimeHealthContext = createContext<RuntimeHealthState | undefined>(undefined);
const RuntimeAccessContext = createContext<RuntimeAccessState | undefined>(undefined);
const RuntimeAlarmContext = createContext<RuntimeAlarmState | undefined>(undefined);
const RuntimeActionsContext = createContext<RuntimeActionsState | undefined>(undefined);

const channelSignature = (channel?: StreamChannel | null) => channel ? [
  channel.id, channel.name, channel.isActive, channel.streamKeyPin, channel.outputLimit,
  channel.cpuLimit, channel.hlsLimit, channel.hlsPlusLimit, channel.socialStreamLimit,
  channel.hasXmltv, channel.hasGracenote, channel.sourceMode, channel.playkastrActive,
  channel.playkastrTierCode,
].join(':') : 'null';

const channelsSignature = (channels: StreamChannel[]) => channels.map(channelSignature).join('|');
const statsSignature = (stats?: StreamStats | null) => stats ? [
  stats.streamId, stats.type, stats.sourceMode, stats.sourceLabel, stats.videoCodec,
  stats.audioCodec, stats.frameRate, stats.msRTT, stats.msReceiveTsbPdDelay,
  stats.packetsReceivedLoss, stats.packetsReceived, stats.remoteAddr,
  stats.bitrateEnforcement?.currentBitrateKbps, stats.bitrateEnforcement?.warningActive,
  stats.bitrateEnforcement?.kickAt, stats.bitrateEnforcement?.kickNoticeActive,
  stats.bitrateEnforcement?.kickReason,
].join(':') : 'null';

const analyticsSignature = (analytics?: StreamAnalytics | null) => analytics ? [
  analytics.streamName, analytics.bitrateKbps, analytics.viewers, analytics.uptimeSec,
  analytics.startTime, analytics.bitrateEnforcement?.warningActive,
  analytics.bitrateEnforcement?.kickAt,
].join(':') : 'null';

const transcoderStatusSignature = (status?: TranscoderRuntimeEnvelope | null) => status ? [
  status.status, status.retryCount, status.lastError, status.lastErrorTime, status.startedAt,
  status.stats?.bitrate, status.stats?.bitrateKbps, status.stats?.currentKbps,
  status.stats?.averageKbps, status.stats?.bytesPerSecond, status.stats?.totalBytesOut,
  status.stats?.lastActivityAt, status.stats?.lastSeenAt,
].join(':') : 'null';

const transcoderSignature = (transcoder?: Transcoder | null) => transcoder ? [
  transcoder.id, transcoder.isActive, transcoder.deliveryMode, transcoder.protocol,
  transcoder.destination, transcoder.platformDistributor, transcoder.videoCodec,
  transcoder.audioCodec, transcoder.pullProtocol, transcoder.pullLabel,
  transcoder.pullPort, transcoder.pullKey, transcoder.pullSecret, transcoder.hlsMount,
  transcoder.playbackUrl, transcoderStatusSignature(transcoder.status),
].join(':') : 'null';

const transcodersSignature = (transcoders: Transcoder[]) => transcoders.map(transcoderSignature).join('|');
const pullStatusSignature = (value?: PullOutputStatusEnvelope | null) => value ? [
  value.outputId, value.isActive, value.pullProtocol, value.pullPort, value.pullKey,
  value.pullSecret, value.pullLatencyMs, value.pullBufferMs, value.status?.status,
  value.status?.pulled, value.status?.processAlive, value.status?.viewerCount,
  value.status?.currentKbps, value.status?.totalBytesOut, value.status?.lastActivityAt,
  value.status?.currentViewer?.ip, value.status?.currentViewer?.lastSeenAt,
  value.destinationHealth?.status, value.destinationHealth?.metrics?.ip,
  value.destinationHealth?.metrics?.trafficKbps, value.destinationHealth?.metrics?.dataMoving,
  value.destinationHealth?.metrics?.updatedAt,
].join(':') : 'null';

const hlsStatusSignature = (value?: HlsSessionStatusEnvelope | null) => value ? [
  value.outputId, value.rev, value.playbackUrl, value.activeSession?.clientIp,
  value.activeSession?.status, value.activeSession?.requestCount,
  value.activeSession?.bytesServed, value.activeSession?.averageKbps,
  value.activeSession?.lastSeenAt,
  value.destinationHealth?.status, value.destinationHealth?.metrics?.ip,
  value.destinationHealth?.metrics?.trafficKbps, value.destinationHealth?.metrics?.dataMoving,
  value.destinationHealth?.metrics?.updatedAt,
].join(':') : 'null';

const healthSignature = (value?: DestinationHealthEnvelope | null) => value ? [
  value.outputId, value.status, value.listenerState, value.metrics?.ip, value.metrics?.trafficKbps,
  value.metrics?.dataMoving, value.metrics?.updatedAt,
].join(':') : 'null';

const runtimeHealthSignature = (value?: RuntimeHealth | null) => value ? [
  value.activeSocketCount, value.runningTranscodersCount, value.streamMonitorRunning,
  value.heapUsed, value.rss, Math.floor(value.uptime),
].join(':') : 'null';

const mapSignature = <T,>(values: Record<number, T>, signature: (value?: T | null) => string) =>
  Object.keys(values).sort((a, b) => Number(a) - Number(b)).map((id) => `${id}:${signature(values[Number(id)])}`).join('|');

const alarmSignature = (alarm?: RuntimeAlarm | null) => alarm ? [
  alarm.channelId, alarm.alarmKey, alarm.raised, alarm.entityType, alarm.outputId, alarm.emittedAt,
].join(':') : 'null';

const alarmsMapSignature = (alarms: Record<string, RuntimeAlarm>) =>
  Object.keys(alarms).sort().map((key) => `${key}:${alarmSignature(alarms[key])}`).join('|');

const mapActiveAlarms = (alarms: RuntimeAlarm[]) =>
  Object.fromEntries((alarms || []).filter((alarm) => alarm?.raised && alarm.alarmKey).map((alarm) => [alarm.alarmKey, alarm]));

const useRequiredContext = <T,>(context: React.Context<T | undefined>, name: string) => {
  const value = useContext(context);
  if (!value) throw new Error(`${name} must be used within RuntimeStatusProvider`);
  return value;
};

export const RuntimeStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const [channels, setChannels] = useState<StreamChannel[]>([]);
  const [activeChannelId, setActiveChannelIdState] = useState<number | null>(null);
  const [transcoders, setTranscoders] = useState<Transcoder[]>([]);
  const [stats, setStats] = useState<StreamStats | null>(null);
  const [analytics, setAnalytics] = useState<StreamAnalytics | null>(null);
  const [pullStatuses, setPullStatuses] = useState<Record<number, PullOutputStatusEnvelope>>({});
  const [hlsStatuses, setHlsStatuses] = useState<Record<number, HlsSessionStatusEnvelope>>({});
  const [outputHealthStatuses, setOutputHealthStatuses] = useState<Record<number, DestinationHealthEnvelope>>({});
  const [serverHealth, setServerHealth] = useState<RuntimeHealth | null>(null);
  const [serverTime, setServerTime] = useState<string | null>(null);
  const [runtimeAlarmsByKey, setRuntimeAlarmsByKey] = useState<Record<string, RuntimeAlarm>>({});
  const [lastAlarmAt, setLastAlarmAt] = useState<string | null>(null);
  const [mutedAlarmOutputIds, setMutedAlarmOutputIds] = useState<Record<number, boolean>>({});
  const [audioAlertState, setAudioAlertState] = useState<AudioAlertState>('unarmed');
  const [loading, setLoading] = useState(true);
  const [isAccountDisabled, setIsAccountDisabled] = useState(false);
  const [isVisible, setIsVisible] = useState(() => document.visibilityState !== 'hidden');
  const channelsRef = useRef<StreamChannel[]>([]);
  const activeChannelIdRef = useRef<number | null>(null);
  const transcodersRef = useRef<Transcoder[]>([]);
  const refreshInFlightRef = useRef(false);
  const alarmAudioRef = useRef<HTMLAudioElement | null>(null);
  const alarmShouldSoundRef = useRef(false);
  const audioArmInFlightRef = useRef(false);
  const lastRuntimeEventAtRef = useRef(0);
  const runtimeErrorRef = useRef(false);
  const refocusSnapshotTimerRef = useRef<number | null>(null);
  const refocusCoalesceUntilRef = useRef(0);
  const pendingNormalTelemetryRef = useRef<Map<string, () => void>>(new Map());
  const pendingNormalTelemetryTimerRef = useRef<number | null>(null);

  const activeChannel = useMemo(
    () => channels.find((channel) => channel.id === activeChannelId) || null,
    [activeChannelId, channels]
  );

  const applySnapshot = useCallback((snapshot: RuntimeSnapshot) => {
    lastRuntimeEventAtRef.current = Date.now();
    runtimeErrorRef.current = false;
    recordRuntimeTelemetryDebug('snapshot:complete', { bytes: estimatePayloadBytes(snapshot) });
    const nextChannels = snapshot.channels || [];
    setChannels((prev) => channelsSignature(prev) === channelsSignature(nextChannels) ? prev : nextChannels);
    channelsRef.current = nextChannels;
    setActiveChannelIdState(snapshot.activeChannel?.id || null);
    activeChannelIdRef.current = snapshot.activeChannel?.id || null;
    setStats((prev) => statsSignature(prev) === statsSignature(snapshot.stats) ? prev : snapshot.stats);
    setAnalytics((prev) => analyticsSignature(prev) === analyticsSignature(snapshot.analytics) ? prev : snapshot.analytics);
    const nextTranscoders = snapshot.transcoders || [];
    transcodersRef.current = nextTranscoders;
    setTranscoders((prev) => transcodersSignature(prev) === transcodersSignature(nextTranscoders) ? prev : nextTranscoders);
    setPullStatuses((prev) => mapSignature(prev, pullStatusSignature) === mapSignature(snapshot.pullStatuses || {}, pullStatusSignature) ? prev : (snapshot.pullStatuses || {}));
    setHlsStatuses((prev) => mapSignature(prev, hlsStatusSignature) === mapSignature(snapshot.hlsStatuses || {}, hlsStatusSignature) ? prev : (snapshot.hlsStatuses || {}));
    setOutputHealthStatuses((prev) => mapSignature(prev, healthSignature) === mapSignature(snapshot.outputHealthStatuses || {}, healthSignature) ? prev : (snapshot.outputHealthStatuses || {}));
    if (snapshot.activeAlarms) {
      const nextAlarms = reconcileAlarmsWithSnapshot(mapActiveAlarms(snapshot.activeAlarms), snapshot);
      setRuntimeAlarmsByKey((prev) => alarmsMapSignature(prev) === alarmsMapSignature(nextAlarms) ? prev : nextAlarms);
      setLastAlarmAt(Object.values(nextAlarms).reduce<string | null>((latest, alarm) =>
        !latest || alarm.emittedAt > latest ? alarm.emittedAt : latest, null));
    } else {
      setRuntimeAlarmsByKey((prev) => {
        const next = reconcileAlarmsWithSnapshot(prev, snapshot);
        return alarmsMapSignature(prev) === alarmsMapSignature(next) ? prev : next;
      });
    }
    setServerHealth((prev) => runtimeHealthSignature(prev) === runtimeHealthSignature(snapshot.health) ? prev : (snapshot.health || null));
    setServerTime((prev) => prev || snapshot.serverTime || null);
    setIsAccountDisabled(false);
    setLoading(false);
  }, []);

  const refreshData = useCallback(async () => {
    if (!user || refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    recordRuntimeTelemetryDebug('snapshot:start');
    try {
      const selected = channelsRef.current.find((channel) => channel.id === activeChannelIdRef.current) || null;
      applySnapshot(await getRuntimeSnapshot(selected));
    } catch (error: any) {
      runtimeErrorRef.current = true;
      if (error?.response?.status === 403) setIsAccountDisabled(true);
      else console.error('Runtime snapshot failed', error);
    } finally {
      refreshInFlightRef.current = false;
      setLoading(false);
    }
  }, [applySnapshot, user]);

  const clearPendingNormalTelemetry = useCallback(() => {
    if (pendingNormalTelemetryTimerRef.current) {
      window.clearTimeout(pendingNormalTelemetryTimerRef.current);
      pendingNormalTelemetryTimerRef.current = null;
    }
    pendingNormalTelemetryRef.current.clear();
  }, []);

  const scheduleRefocusSnapshot = useCallback(() => {
    if (!user) return;
    if (refocusSnapshotTimerRef.current) {
      window.clearTimeout(refocusSnapshotTimerRef.current);
      refocusSnapshotTimerRef.current = null;
    }
    const socketIsFresh = isConnected && Date.now() - lastRuntimeEventAtRef.current < RUNTIME_EVENT_STALE_MS;
    if (socketIsFresh && !runtimeErrorRef.current) {
      recordRuntimeTelemetryDebug('refocus:snapshot-skipped');
      return;
    }
    refocusSnapshotTimerRef.current = window.setTimeout(() => {
      refocusSnapshotTimerRef.current = null;
      const socketRecovered = isConnected && Date.now() - lastRuntimeEventAtRef.current < RUNTIME_EVENT_STALE_MS;
      if (socketRecovered && !runtimeErrorRef.current) {
        recordRuntimeTelemetryDebug('refocus:snapshot-skipped-after-delay');
        return;
      }
      if (document.visibilityState !== 'hidden') void refreshData();
    }, REFOCUS_SNAPSHOT_DELAY_MS);
  }, [isConnected, refreshData, user]);

  const setActiveChannelId = useCallback((id: number) => {
    if (activeChannelIdRef.current === id) return;
    activeChannelIdRef.current = id;
    setActiveChannelIdState(id);
    setRuntimeAlarmsByKey({});
    setLastAlarmAt(null);
    const selected = channelsRef.current.find((channel) => channel.id === id);
    socket?.emit('runtime:snapshot:request', { channelId: selected?.channelHashId || selected?.hashId || id });
    getRuntimeSnapshot(selected || id).then(applySnapshot).catch((error) => console.error('Channel runtime snapshot failed', error));
  }, [applySnapshot, socket]);

  const clearOutputAlarmMute = useCallback((outputId: number) => {
    localStorage.removeItem(`outputAlarmMuted:${outputId}`);
    setMutedAlarmOutputIds((prev) => {
      if (!prev[outputId]) return prev;
      const next = { ...prev };
      delete next[outputId];
      return next;
    });
  }, []);

  const toggleOutputAlarmMute = useCallback((outputId: number) => {
    setMutedAlarmOutputIds((prev) => {
      const muted = prev[outputId] !== true;
      localStorage.setItem(`outputAlarmMuted:${outputId}`, String(muted));
      return { ...prev, [outputId]: muted };
    });
  }, []);

  const clearRuntimeAlarmKeys = useCallback((...alarmKeys: string[]) => {
    setRuntimeAlarmsByKey((prev) => {
      const keysToDelete = alarmKeys.filter((alarmKey) => prev[alarmKey]);
      if (!keysToDelete.length) return prev;
      const next = { ...prev };
      keysToDelete.forEach((alarmKey) => delete next[alarmKey]);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user) {
      channelsRef.current = [];
      activeChannelIdRef.current = null;
      setChannels([]);
      setActiveChannelIdState(null);
      transcodersRef.current = [];
      setTranscoders([]);
      setStats(null);
      setAnalytics(null);
      setPullStatuses({});
      setHlsStatuses({});
      setOutputHealthStatuses({});
      setServerHealth(null);
      setServerTime(null);
      setRuntimeAlarmsByKey({});
      setLastAlarmAt(null);
      setMutedAlarmOutputIds({});
      setAudioAlertState('unarmed');
      setLoading(false);
      return;
    }
    setLoading(true);
    void refreshData();
  }, [refreshData, user]);

  useEffect(() => {
    const next: Record<number, boolean> = {};
    transcoders.forEach((transcoder) => {
      if (localStorage.getItem(`outputAlarmMuted:${transcoder.id}`) === 'true') next[transcoder.id] = true;
    });
    setMutedAlarmOutputIds((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      return prevKeys.length === nextKeys.length && prevKeys.every((id) => prev[Number(id)] === next[Number(id)]) ? prev : next;
    });
  }, [transcoders]);

  useEffect(() => {
    if (!alarmAudioRef.current) {
      alarmAudioRef.current = new Audio('/alarm.mp3');
      alarmAudioRef.current.loop = true;
    }
    const shouldSound = Object.values(runtimeAlarmsByKey).some((alarm) => !alarm.outputId || mutedAlarmOutputIds[alarm.outputId] !== true);
    alarmShouldSoundRef.current = shouldSound;
    if (shouldSound) {
      alarmAudioRef.current.play()
        .then(() => setAudioAlertState('armed'))
        .catch(() => setAudioAlertState('blocked'));
    }
    else {
      alarmAudioRef.current.pause();
      alarmAudioRef.current.currentTime = 0;
    }
    return () => alarmAudioRef.current?.pause();
  }, [mutedAlarmOutputIds, runtimeAlarmsByKey]);

  const armAudioAlerts = useCallback(() => {
    const audio = alarmAudioRef.current;
    if (!audio || audioArmInFlightRef.current) return;
    audioArmInFlightRef.current = true;
    audio.muted = true;
    audio.play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
        setAudioAlertState('armed');
        if (alarmShouldSoundRef.current) {
          audio.play().catch(() => setAudioAlertState('blocked'));
        }
      })
      .catch(() => {
        audio.muted = false;
        setAudioAlertState('blocked');
      })
      .finally(() => { audioArmInFlightRef.current = false; });
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
      armAudioAlerts();
    };
    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, [armAudioAlerts]);

  useEffect(() => {
    const onVisibilityChange = () => {
      const nextVisible = document.visibilityState !== 'hidden';
      setIsVisible(nextVisible);
      if (!nextVisible) {
        if (refocusSnapshotTimerRef.current) {
          window.clearTimeout(refocusSnapshotTimerRef.current);
          refocusSnapshotTimerRef.current = null;
        }
        clearPendingNormalTelemetry();
        return;
      }
      refocusCoalesceUntilRef.current = Date.now() + REFOCUS_SOCKET_COALESCE_MS;
      recordRuntimeTelemetryDebug('visibility:restored');
      scheduleRefocusSnapshot();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (refocusSnapshotTimerRef.current) {
        window.clearTimeout(refocusSnapshotTimerRef.current);
        refocusSnapshotTimerRef.current = null;
      }
      clearPendingNormalTelemetry();
    };
  }, [clearPendingNormalTelemetry, scheduleRefocusSnapshot]);

  useEffect(() => {
    if (!user || !isVisible) return;
    const interval = window.setInterval(refreshData, isConnected ? 90000 : 10000);
    return () => window.clearInterval(interval);
  }, [isConnected, isVisible, refreshData, user]);

  useEffect(() => {
    if (!socket) return;
    const forActiveChannel = (payload: { channelId?: number }) => !payload.channelId || Number(payload.channelId) === activeChannelIdRef.current;
    const touchRuntimeEvent = () => {
      const now = Date.now();
      lastRuntimeEventAtRef.current = now;
      runtimeErrorRef.current = false;
      if (now < refocusCoalesceUntilRef.current) recordRuntimeTelemetryDebug('refocus:socket-event');
    };
    const flushNormalTelemetry = () => {
      pendingNormalTelemetryTimerRef.current = null;
      const updates = Array.from(pendingNormalTelemetryRef.current.values());
      pendingNormalTelemetryRef.current.clear();
      if (updates.length) recordRuntimeTelemetryDebug('refocus:socket-flush', { updates: updates.length });
      updates.forEach((apply) => apply());
    };
    const queueNormalTelemetry = (key: string, apply: () => void) => {
      touchRuntimeEvent();
      const delay = refocusCoalesceUntilRef.current - Date.now();
      if (delay <= 0) {
        apply();
        return;
      }
      pendingNormalTelemetryRef.current.set(key, apply);
      recordRuntimeTelemetryDebug('refocus:socket-coalesced', { key });
      if (!pendingNormalTelemetryTimerRef.current) {
        pendingNormalTelemetryTimerRef.current = window.setTimeout(flushNormalTelemetry, delay);
      }
    };
    const patchStats = (next: StreamStats) => {
      setStats((prev) => statsSignature(prev) === statsSignature(next) ? prev : next);
      if (next?.type) clearRuntimeAlarmKeys('stream-offline');
      if (next?.bitrateEnforcement?.warningActive !== true) clearRuntimeAlarmKeys('bitrate-warning');
      if (next?.bitrateEnforcement?.kickNoticeActive !== true) clearRuntimeAlarmKeys('bitrate-shutdown');
    };
    const patchAnalytics = (next: StreamAnalytics) => setAnalytics((prev) => analyticsSignature(prev) === analyticsSignature(next) ? prev : next);
    const clearRecoveredOutputAlarms = (outputId: number) => clearRuntimeAlarmKeys(...getOutputAlarmKeys(outputId));
    const applyOutputHealthAlarm = (outputId: number, health?: DestinationHealthEnvelope | null) => {
      const output = transcodersRef.current.find((entry) => entry.id === outputId);
      if (!output) return;
      setRuntimeAlarmsByKey((prev) => {
        const next = reconcileOutputHealthAlarm(prev, output, health);
        return alarmsMapSignature(prev) === alarmsMapSignature(next) ? prev : next;
      });
      if (output.isActive && health?.status === 'disconnected') {
        setLastAlarmAt(health.metrics?.updatedAt || new Date().toISOString());
      }
    };
    const patchPull = (outputId: number, next: PullOutputStatusEnvelope) => {
      setPullStatuses((prev) => pullStatusSignature(prev[outputId]) === pullStatusSignature(next) ? prev : { ...prev, [outputId]: next });
      if (next?.destinationHealth) applyOutputHealthAlarm(outputId, next.destinationHealth);
    };
    const patchHls = (outputId: number, next: HlsSessionStatusEnvelope) => {
      setHlsStatuses((prev) => hlsStatusSignature(prev[outputId]) === hlsStatusSignature(next) ? prev : { ...prev, [outputId]: next });
      if (next?.destinationHealth) {
        setOutputHealthStatuses((prev) => healthSignature(prev[outputId]) === healthSignature(next.destinationHealth) ? prev : { ...prev, [outputId]: next.destinationHealth! });
        applyOutputHealthAlarm(outputId, next.destinationHealth);
      }
    };
    const patchHealth = (outputId: number, next: DestinationHealthEnvelope) => {
      setOutputHealthStatuses((prev) => healthSignature(prev[outputId]) === healthSignature(next) ? prev : { ...prev, [outputId]: next });
      applyOutputHealthAlarm(outputId, next);
    };
    const clearOutputRuntime = (outputId: number, remove = false) => {
      clearRecoveredOutputAlarms(outputId);
      clearOutputAlarmMute(outputId);
      if (remove) {
        setPullStatuses((prev) => {
          if (!prev[outputId]) return prev;
          const next = { ...prev };
          delete next[outputId];
          return next;
        });
        setHlsStatuses((prev) => {
          if (!prev[outputId]) return prev;
          const next = { ...prev };
          delete next[outputId];
          return next;
        });
        setOutputHealthStatuses((prev) => {
          if (!prev[outputId]) return prev;
          const next = { ...prev };
          delete next[outputId];
          return next;
        });
        return;
      }
      patchHealth(outputId, {
        outputId,
        status: 'disabled',
        metrics: {
          ip: null,
          trafficKbps: 0,
          dataMoving: false,
          validIp: false,
          positiveTraffic: false,
          source: 'disabled',
        },
      });
    };

    const onSnapshot = (snapshot: RuntimeSnapshot) => applySnapshot(snapshot);
    const onStats = (payload: { channelId: number; stats: StreamStats }) => { if (forActiveChannel(payload)) queueNormalTelemetry('stats', () => patchStats(payload.stats)); };
    const onAnalytics = (payload: { channelId: number; analytics: StreamAnalytics }) => { if (forActiveChannel(payload)) queueNormalTelemetry('analytics', () => patchAnalytics(payload.analytics)); };
    const onChannelUpdate = (payload: { channelId: number; channel: StreamChannel }) => setChannels((prev) => {
      touchRuntimeEvent();
      const next = prev.map((channel) => channel.id === payload.channelId ? { ...channel, ...payload.channel } : channel);
      channelsRef.current = next;
      return channelsSignature(prev) === channelsSignature(next) ? prev : next;
    });
    const onTranscoderStatuses = (payload: { channelId: number; statuses: TranscoderStatusMap }) => {
      if (!forActiveChannel(payload)) return;
      queueNormalTelemetry('transcoder-statuses', () => {
        Object.entries(payload.statuses || {}).forEach(([outputId, status]) => {
          if (!['failed', 'error', 'retrying'].includes(String(status?.status || '').toLowerCase())) clearRuntimeAlarmKeys(`transcoder-${outputId}`);
        });
        setTranscoders((prev) => {
          let changed = false;
          const next = prev.map((transcoder) => {
            const status = payload.statuses?.[transcoder.id];
            if (!status || transcoderStatusSignature(transcoder.status) === transcoderStatusSignature(status)) return transcoder;
            changed = true;
            return { ...transcoder, status };
          });
          return changed ? next : prev;
        });
      });
    };
    const onTranscoderUpdate = (payload: { channelId: number; transcoder?: Transcoder | null; deletedId?: number | null }) => {
      if (!forActiveChannel(payload)) return;
      touchRuntimeEvent();
      if (payload.deletedId) clearOutputRuntime(payload.deletedId, true);
      else if (payload.transcoder?.isActive === false) clearOutputRuntime(payload.transcoder.id);
      setTranscoders((prev) => {
        if (payload.deletedId) {
          const next = prev.filter((transcoder) => transcoder.id !== payload.deletedId);
          transcodersRef.current = next;
          return next;
        }
        if (!payload.transcoder) return prev;
        const existing = prev.find((transcoder) => transcoder.id === payload.transcoder?.id);
        if (!existing) {
          const next = [...prev, payload.transcoder];
          transcodersRef.current = next;
          return next;
        }
        if (transcoderSignature(existing) === transcoderSignature(payload.transcoder)) return prev;
        const next = prev.map((transcoder) => transcoder.id === payload.transcoder?.id ? { ...transcoder, ...payload.transcoder } : transcoder);
        transcodersRef.current = next;
        return next;
      });
    };
    const onPullStatus = (payload: { channelId: number; outputId: number; status: PullOutputStatusEnvelope }) => { if (forActiveChannel(payload)) queueNormalTelemetry(`pull:${payload.outputId}`, () => patchPull(payload.outputId, payload.status)); };
    const onHlsStatus = (payload: { channelId: number; outputId: number; status: HlsSessionStatusEnvelope }) => { if (forActiveChannel(payload)) queueNormalTelemetry(`hls:${payload.outputId}`, () => patchHls(payload.outputId, payload.status)); };
    const onOutputHealth = (payload: { channelId: number; outputId: number; status: DestinationHealthEnvelope }) => { if (forActiveChannel(payload)) queueNormalTelemetry(`health:${payload.outputId}`, () => patchHealth(payload.outputId, payload.status)); };
    const onStreamState = (payload: { channelId: number; stats: StreamStats }) => { if (forActiveChannel(payload)) { touchRuntimeEvent(); patchStats(payload.stats); } };
    const onBitrateState = (payload: { channelId: number; stats: StreamStats }) => { if (forActiveChannel(payload)) { touchRuntimeEvent(); patchStats(payload.stats); } };
    const onTranscoderState = (payload: { channelId: number; outputId: number; status: TranscoderRuntimeEnvelope }) => {
      if (!forActiveChannel(payload)) return;
      touchRuntimeEvent();
      if (!['failed', 'error', 'retrying'].includes(String(payload.status?.status || '').toLowerCase())) clearRuntimeAlarmKeys(`transcoder-${payload.outputId}`);
      setTranscoders((prev) => {
        let changed = false;
        const next = prev.map((transcoder) => {
          if (transcoder.id !== payload.outputId || transcoderStatusSignature(transcoder.status) === transcoderStatusSignature(payload.status)) return transcoder;
          changed = true;
          return { ...transcoder, status: payload.status };
        });
        return changed ? next : prev;
      });
    };
    const onOutputState = (payload: { channelId: number; outputId: number; status: DestinationHealthEnvelope }) => { if (forActiveChannel(payload)) { touchRuntimeEvent(); patchHealth(payload.outputId, payload.status); } };
    const onAlarmRaised = (payload: RuntimeAlarm) => {
      if (!forActiveChannel(payload)) return;
      touchRuntimeEvent();
      setRuntimeAlarmsByKey((prev) => alarmSignature(prev[payload.alarmKey]) === alarmSignature(payload) ? prev : { ...prev, [payload.alarmKey]: payload });
      setLastAlarmAt(payload.emittedAt || new Date().toISOString());
    };
    const onAlarmCleared = (payload: RuntimeAlarm) => {
      if (!forActiveChannel(payload)) return;
      touchRuntimeEvent();
      setRuntimeAlarmsByKey((prev) => {
        if (!prev[payload.alarmKey]) return prev;
        const next = { ...prev };
        delete next[payload.alarmKey];
        return next;
      });
      if (payload.outputId) clearOutputAlarmMute(payload.outputId);
    };
    const onRuntimeError = () => {
      runtimeErrorRef.current = true;
      if (document.visibilityState !== 'hidden') scheduleRefocusSnapshot();
    };

    socket.on('runtime:snapshot', onSnapshot);
    socket.on('stream:stats:update', onStats);
    socket.on('stream:analytics:update', onAnalytics);
    socket.on('channel:update', onChannelUpdate);
    socket.on('transcoder:update', onTranscoderUpdate);
    socket.on('transcoder:status:update', onTranscoderStatuses);
    socket.on('pull:status:update', onPullStatus);
    socket.on('hls:session:update', onHlsStatus);
    socket.on('output:health:update', onOutputHealth);
    socket.on('stream:state:changed', onStreamState);
    socket.on('bitrate:warning', onBitrateState);
    socket.on('bitrate:recovered', onBitrateState);
    socket.on('transcoder:state:changed', onTranscoderState);
    socket.on('output:state:changed', onOutputState);
    socket.on('alarm:raised', onAlarmRaised);
    socket.on('alarm:cleared', onAlarmCleared);
    socket.on('runtime:error', onRuntimeError);
    if (isConnected) {
      const selected = channelsRef.current.find((channel) => channel.id === activeChannelIdRef.current);
      socket.emit('runtime:snapshot:request', { channelId: selected?.channelHashId || selected?.hashId });
    }
    return () => {
      socket.off('runtime:snapshot', onSnapshot);
      socket.off('stream:stats:update', onStats);
      socket.off('stream:analytics:update', onAnalytics);
      socket.off('channel:update', onChannelUpdate);
      socket.off('transcoder:update', onTranscoderUpdate);
      socket.off('transcoder:status:update', onTranscoderStatuses);
      socket.off('pull:status:update', onPullStatus);
      socket.off('hls:session:update', onHlsStatus);
      socket.off('output:health:update', onOutputHealth);
      socket.off('stream:state:changed', onStreamState);
      socket.off('bitrate:warning', onBitrateState);
      socket.off('bitrate:recovered', onBitrateState);
      socket.off('transcoder:state:changed', onTranscoderState);
      socket.off('output:state:changed', onOutputState);
      socket.off('alarm:raised', onAlarmRaised);
      socket.off('alarm:cleared', onAlarmCleared);
      socket.off('runtime:error', onRuntimeError);
      clearPendingNormalTelemetry();
    };
  }, [applySnapshot, clearOutputAlarmMute, clearPendingNormalTelemetry, clearRuntimeAlarmKeys, isConnected, scheduleRefocusSnapshot, socket]);

  const channelValue = useMemo<RuntimeChannelState>(() => ({ channels, activeChannel, setActiveChannelId }), [activeChannel, channels, setActiveChannelId]);
  const statsValue = useMemo<RuntimeStatsState>(() => ({ stats, analytics, isStreamLive: !!stats?.type }), [analytics, stats]);
  const outputValue = useMemo<RuntimeOutputState>(() => ({ transcoders, pullStatuses, hlsStatuses, outputHealthStatuses }), [hlsStatuses, outputHealthStatuses, pullStatuses, transcoders]);
  const healthValue = useMemo<RuntimeHealthState>(() => ({ serverHealth, serverTime, loading, isConnected, isAccountDisabled }), [isAccountDisabled, isConnected, loading, serverHealth, serverTime]);
  const accessValue = useMemo<RuntimeAccessState>(() => ({ isAccountDisabled }), [isAccountDisabled]);
  const alarmValue = useMemo<RuntimeAlarmState>(() => ({
    runtimeAlarmsByKey,
    activeAlarms: Object.values(runtimeAlarmsByKey),
    lastAlarmAt,
    mutedAlarmOutputIds,
    audioAlertState,
    armAudioAlerts,
    toggleOutputAlarmMute,
    clearOutputAlarmMute,
  }), [armAudioAlerts, audioAlertState, clearOutputAlarmMute, lastAlarmAt, mutedAlarmOutputIds, runtimeAlarmsByKey, toggleOutputAlarmMute]);
  const actionsValue = useMemo<RuntimeActionsState>(() => ({ refreshData }), [refreshData]);

  return (
    <RuntimeActionsContext.Provider value={actionsValue}>
      <RuntimeAccessContext.Provider value={accessValue}>
        <RuntimeHealthContext.Provider value={healthValue}>
          <RuntimeAlarmContext.Provider value={alarmValue}>
            <RuntimeChannelContext.Provider value={channelValue}>
              <RuntimeStatsContext.Provider value={statsValue}>
                <RuntimeOutputContext.Provider value={outputValue}>
                  {children}
                </RuntimeOutputContext.Provider>
              </RuntimeStatsContext.Provider>
            </RuntimeChannelContext.Provider>
          </RuntimeAlarmContext.Provider>
        </RuntimeHealthContext.Provider>
      </RuntimeAccessContext.Provider>
    </RuntimeActionsContext.Provider>
  );
};

export const useRuntimeChannel = () => useRequiredContext(RuntimeChannelContext, 'useRuntimeChannel');
export const useRuntimeStats = () => useRequiredContext(RuntimeStatsContext, 'useRuntimeStats');
export const useRuntimeOutput = () => useRequiredContext(RuntimeOutputContext, 'useRuntimeOutput');
export const useRuntimeHealth = () => useRequiredContext(RuntimeHealthContext, 'useRuntimeHealth');
export const useRuntimeAccess = () => useRequiredContext(RuntimeAccessContext, 'useRuntimeAccess');
export const useRuntimeAlarms = () => useRequiredContext(RuntimeAlarmContext, 'useRuntimeAlarms');
export const useRuntimeActions = () => useRequiredContext(RuntimeActionsContext, 'useRuntimeActions');

export const useRuntimeStatus = (): RuntimeStatusState => ({
  ...useRuntimeChannel(),
  ...useRuntimeStats(),
  ...useRuntimeOutput(),
  ...useRuntimeHealth(),
  ...useRuntimeAlarms(),
  ...useRuntimeActions(),
});
